import {
    getAssessmentForms,
    getAssessmentFormDetail,
    getAssessmentResponseDetail,
    createAssessmentForm,
    addAssessmentQuestion,
    addAssessmentOption,
    publishAssessmentForm,
    startAssessmentResponse,
    submitAssessmentAnswer,
    completeAssessmentResponse,
    submitAssessmentForm,
    exportAssessmentFormResponses,
} from '../../../../Utils/apiService';

export interface AssessmentFormSummary {
    formId: string;
    title: string;
    formType: string;
    status: string;
    unlockRule?: string;
    questionCount: number;
    enrolledCount: number;
    respondedCount: number;
    myResponseStatus?: string;
}

// Confirmed real response shape (live sandbox call, role=Student):
// { forms: [ { formId, title, formType, status, unlockRule,
//   unlockProgressThreshold, questionCount, enrolledCount, respondedCount,
//   myResponseStatus, isGraded } ] }
// Locked forms are confirmed omitted server-side for role=Student (matches
// the API doc's "Students see unlocked only") — no client-side lock
// gating needed on the list.
export const fetchAssessmentForms = async (
    batchId: string,
    contactId: string,
    role: 'Faculty' | 'Student'
): Promise<AssessmentFormSummary[]> => {
    if (!batchId || !contactId) return [];
    try {
        const response = await getAssessmentForms(batchId, contactId, role);
        const rawList: any[] = Array.isArray(response?.forms) ? response.forms : [];
        return rawList.map((item): AssessmentFormSummary => ({
            formId: item.formId || '',
            title: item.title || 'Untitled form',
            formType: item.formType || '',
            status: item.status || '',
            unlockRule: item.unlockRule || undefined,
            questionCount: item.questionCount ?? 0,
            enrolledCount: item.enrolledCount ?? 0,
            respondedCount: item.respondedCount ?? 0,
            myResponseStatus: item.myResponseStatus || undefined,
        })).filter((f) => f.formId);
    } catch (error) {
        console.error('Error fetching assessment forms:', error);
        return [];
    }
};

export interface AssessmentOption {
    optionId: string;
    optionText: string;
    order: number;
}

export interface AssessmentQuestion {
    questionId: string;
    questionText: string;
    answerType: string;
    order: number;
    isRequired: boolean;
    options: AssessmentOption[];
}

export interface RespondentSummary {
    responseId: string;
    respondentName: string;
    respondentId: string;
    status: string;
    completedDate?: string;
}

export interface AssessmentFormDetail extends AssessmentFormSummary {
    questions: AssessmentQuestion[];
    myResponseId?: string;
    // Faculty-only — every respondent's name/status/responseId, per the API
    // doc ("Pass role=Faculty to also get a responses array"). Confirmed
    // live shape: [{status, responseId, respondentName, respondentId,
    // formId, completedDate}].
    responses?: RespondentSummary[];
}

// Real response shape (confirmed via a live GET call):
// { form: {formId, title, formType, status, unlockRule, questionCount,
//   enrolledCount, respondedCount, myResponseStatus, ...}, questions: [
//   {questionId, questionText, answerType, order, isRequired, options: [
//   {optionId, optionText, order}]}], myResponseStatus, myResponseId,
//   responses: [ {...} ] (Faculty only) }
// Questions live at the top level, NOT nested under `form` — form only
// carries the form's own metadata.
export const fetchAssessmentFormDetail = async (
    formId: string,
    contactId: string,
    role: 'Faculty' | 'Student'
): Promise<AssessmentFormDetail | null> => {
    if (!formId || !contactId) return null;
    try {
        const response = await getAssessmentFormDetail(formId, contactId, role);
        const form = response?.form;
        if (!form) return null;
        const rawQuestions: any[] = Array.isArray(response?.questions) ? response.questions : [];
        const questions: AssessmentQuestion[] = rawQuestions
            .map((q): AssessmentQuestion => {
                const rawOptions: any[] = Array.isArray(q?.options) ? q.options : [];
                return {
                    questionId: q.questionId || '',
                    questionText: q.questionText || '',
                    answerType: q.answerType || '',
                    order: q.order ?? 0,
                    isRequired: !!q.isRequired,
                    options: rawOptions
                        .map((o): AssessmentOption => ({
                            optionId: o.optionId || '',
                            optionText: o.optionText || '',
                            order: o.order ?? 0,
                        }))
                        .filter((o) => o.optionId)
                        .sort((a, b) => a.order - b.order),
                };
            })
            .filter((q) => q.questionId)
            .sort((a, b) => a.order - b.order);

        const rawResponses: any[] = Array.isArray(response?.responses) ? response.responses : [];
        const responses: RespondentSummary[] = rawResponses
            .map((r): RespondentSummary => ({
                responseId: r.responseId || '',
                respondentName: r.respondentName || 'Unknown student',
                respondentId: r.respondentId || '',
                status: r.status || '',
                completedDate: r.completedDate || undefined,
            }))
            .filter((r) => r.responseId);

        return {
            formId: form.formId || '',
            title: form.title || 'Untitled form',
            formType: form.formType || '',
            status: form.status || '',
            unlockRule: form.unlockRule || undefined,
            questionCount: questions.length,
            enrolledCount: form.enrolledCount ?? 0,
            respondedCount: form.respondedCount ?? 0,
            myResponseStatus: response?.myResponseStatus || undefined,
            myResponseId: response?.myResponseId || undefined,
            questions,
            responses: responses.length > 0 ? responses : undefined,
        };
    } catch (error) {
        console.error('Error fetching assessment form detail:', error);
        return null;
    }
};

export interface ResponseAnswer {
    questionText: string;
    answerType: string;
    answerText: string;
}

export interface ResponseDetail {
    responseId: string;
    respondentName: string;
    status: string;
    formTitle: string;
    formType: string;
    batchName: string;
    answers: ResponseAnswer[];
}

// Real response shape per the API doc's example:
// { responseId, respondentName, status, formTitle, formType, batchName,
//   answers: [ {questionText, answerType, answerText} ] }
// answerText holds the resolved option text for Multiple Choice (not an
// optionId) and the typed text for Open Text — the same field either way.
export const fetchAssessmentResponseDetail = async (
    responseId: string,
    role: 'Faculty' | 'Student'
): Promise<ResponseDetail | null> => {
    if (!responseId) return null;
    try {
        const response = await getAssessmentResponseDetail(responseId, role);
        if (!response?.responseId) return null;
        const rawAnswers: any[] = Array.isArray(response?.answers) ? response.answers : [];
        return {
            responseId: response.responseId,
            respondentName: response.respondentName || 'Unknown student',
            status: response.status || '',
            formTitle: response.formTitle || '',
            formType: response.formType || '',
            batchName: response.batchName || '',
            answers: rawAnswers.map((a): ResponseAnswer => ({
                questionText: a.questionText || '',
                answerType: a.answerType || '',
                answerText: a.answerText || '',
            })),
        };
    } catch (error) {
        console.error('Error fetching assessment response detail:', error);
        return null;
    }
};

// Faculty-side authoring — thin wrappers, Faculty-only per the API docs.
export const createForm = async (input: {
    contactId: string;
    batchId: string;
    formType: string;
    title: string;
    unlockRule: 'Immediate' | 'At_Progress_Threshold' | 'Manual';
    unlockThreshold?: number;
}) => createAssessmentForm(input);

export const addQuestion = async (input: {
    contactId: string;
    formId: string;
    questionText: string;
    answerType: string;
    order: number;
    isRequired: boolean;
}) => addAssessmentQuestion(input);

export const addOption = async (input: {
    contactId: string;
    questionId: string;
    optionText: string;
    order: number;
}) => addAssessmentOption(input);

export const publishForm = async (input: { contactId: string; formId: string }) =>
    publishAssessmentForm(input);

// Student-side (and faculty-preview) attempt-taking — thin wrappers, both roles.
export const startResponse = async (input: {
    contactId: string;
    formId: string;
    role: 'Faculty' | 'Student';
}) => startAssessmentResponse(input);

export const submitAnswer = async (input: {
    contactId: string;
    responseId: string;
    questionId: string;
    selectedOptionId?: string;
    answerText?: string;
}) => submitAssessmentAnswer(input);

export const completeResponse = async (input: { contactId: string; responseId: string; role: 'Faculty' | 'Student' }) =>
    completeAssessmentResponse(input);

// Recommended path for short forms (a few questions, nothing worth
// resuming) — collects every answer locally, then submits + completes in
// one call instead of startResponse + submitAnswer-per-question + completeResponse.
export const submitForm = async (input: {
    contactId: string;
    role: 'Faculty' | 'Student';
    formId: string;
    answers: { questionId: string; selectedOptionId?: string; answerText?: string }[];
}) => submitAssessmentForm(input);


// Faculty CSV export of a form's responses. The endpoint returns raw CSV
// text, so the download is assembled client-side rather than following a
// server-set Content-Disposition. Returns false when the export came back
// empty so the caller can tell the user instead of saving a blank file.
export const downloadFormResponsesCsv = async (formId: string, formTitle: string): Promise<boolean> => {
    const csv = await exportAssessmentFormResponses(formId);
    if (!csv || !csv.trim()) return false;

    // Excel needs the BOM to read UTF-8 (student names here are non-ASCII).
    const blob = new Blob(['\ufeff', csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const safeTitle = (formTitle || 'assessment-responses').replace(/[^\w\d\-_ ]+/g, '').trim() || 'assessment-responses';
    const link = document.createElement('a');
    link.href = url;
    link.download = `${safeTitle}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    return true;
};
